import React, { useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { Layout } from '@/components/layout/Layout';
import { toast } from 'sonner';
import axios from 'axios';
import { Utensils, Check, Handshake, UserX, CreditCard, FileText, FileX } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RestaurantRequestPage() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [consent, setConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    restaurant_name: '',
    contact_name: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    pizza_type_needed: '',
    oven_type: '',
    experience_required: '',
    salary_range: '',
    accommodation_provided: false,
    urgency: '',
    additional_notes: ''
  });

  const experienceLevels = [
    { value: '0-1', label: t.experienceLevels['0-1'] },
    { value: '1-2', label: t.experienceLevels['1-2'] },
    { value: '2-5', label: t.experienceLevels['2-5'] },
    { value: '5-10', label: t.experienceLevels['5-10'] },
    { value: '10+', label: t.experienceLevels['10+'] }
  ];

  const pizzaTypes = [
    { value: 'napoletana', label: t.pizzaTypes.napoletana },
    { value: 'romana', label: t.pizzaTypes.romana },
    { value: 'contemporanea', label: t.pizzaTypes.contemporanea },
    { value: 'teglia', label: t.pizzaTypes.teglia },
    { value: 'pinsa', label: t.pizzaTypes.pinsa }
  ];

  const ovenTypes = [
    { value: 'legna', label: t.ovenTypes.legna },
    { value: 'gas', label: t.ovenTypes.gas },
    { value: 'elettrico', label: t.ovenTypes.elettrico },
    { value: 'misto', label: t.ovenTypes.misto }
  ];

  const urgencyLevels = [
    { value: 'immediate', label: t.urgencyLevels.immediate },
    { value: '1month', label: t.urgencyLevels['1month'] },
    { value: '3months', label: t.urgencyLevels['3months'] },
    { value: 'flexible', label: t.urgencyLevels.flexible }
  ];

  const countries = [
    { value: 'italy', label: t.countries.italy },
    { value: 'france', label: t.countries.france },
    { value: 'germany', label: t.countries.germany },
    { value: 'switzerland', label: t.countries.switzerland },
    { value: 'spain', label: t.countries.spain },
    { value: 'uk', label: t.countries.uk },
    { value: 'netherlands', label: t.countries.netherlands },
    { value: 'belgium', label: t.countries.belgium },
    { value: 'austria', label: t.countries.austria },
    { value: 'sweden', label: t.countries.sweden }
  ];

  const transparencyItems = [
    {
      icon: Handshake,
      title: t.about.transparency1Title,
      desc: t.about.transparency1Desc
    },
    {
      icon: UserX,
      title: t.about.transparency2Title,
      desc: t.about.transparency2Desc
    },
    {
      icon: CreditCard,
      title: t.about.transparency3Title,
      desc: t.about.transparency3Desc
    },
    {
      icon: FileText,
      title: t.about.transparency4Title,
      desc: t.about.transparency4Desc
    },
    {
      icon: FileX,
      title: t.about.transparency5Title,
      desc: t.about.transparency5Desc
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/restaurants/request`, formData);
      setSubmitted(true);
      toast.success(t.restaurantForm.success);
    } catch (error) {
      const message = error.response?.data?.detail || t.restaurantForm.error;
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Layout>
        <div className="min-h-[80vh] flex items-center justify-center px-6" data-testid="submission-success">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
              <Check className="text-primary" size={40} />
            </div>
            <h1 className="text-3xl font-serif text-foreground mb-4">
              {t.restaurantForm.success}
            </h1>
            <p className="text-muted-foreground">
              Analizzerò la tua richiesta e ti contatterò presto con i profili selezionati.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="py-12 md:py-24 relative min-h-screen">
        <div 
          className="fixed inset-0 z-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: 'url("https://static.prod-images.emergentagent.com/jobs/d9cf0590-fa7d-4aa8-865e-545c02b78803/images/49b1c8e8fa9226d8b7a281683e83ad4e1217d62a9e7885964a93658ad785fd76.png")' }}
        />
        <div className="fixed inset-0 z-0 bg-black/70" />
        <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Utensils className="text-primary" size={32} />
            </div>
            <h1 className="text-3xl md:text-5xl font-serif text-foreground mb-4" data-testid="restaurant-form-title">
              {t.restaurantForm.title}
            </h1>
            <p className="text-muted-foreground text-lg">
              {t.restaurantForm.subtitle}
            </p>
          </div>

          {/* Transparency Section */}
          <div className="bg-card border border-primary/10 rounded-sm p-6 md:p-8 mb-8" data-testid="transparency-section">
            <h2 className="text-xl font-serif text-foreground mb-6 text-center">
              {t.about.transparencyTitle} - {t.about.transparencySubtitle}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {transparencyItems.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 p-4 bg-background border border-primary/5 rounded-sm"
                >
                  <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="text-primary" size={20} />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-foreground mb-1">
                      {item.title}
                    </h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-8" data-testid="restaurant-request-form">
            {/* Restaurant Info */}
            <div className="bg-card border border-primary/10 rounded-sm p-6 md:p-8">
              <h2 className="text-xl font-serif text-foreground mb-6 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-sm">1</span>
                {t.restaurantForm.section1}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t.restaurantForm.restaurantName} *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.restaurant_name}
                    onChange={(e) => setFormData({ ...formData, restaurant_name: e.target.value })}
                    className="form-input w-full"
                    data-testid="input-restaurant-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t.restaurantForm.contactName} *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    className="form-input w-full"
                    data-testid="input-contact-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t.restaurantForm.email} *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="form-input w-full"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t.restaurantForm.phone} *
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="form-input w-full"
                    data-testid="input-phone"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t.restaurantForm.country} *
                  </label>
                  <select
                    required
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="form-select w-full"
                    data-testid="select-country"
                  >
                    <option value="">Seleziona...</option>
                    {countries.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t.restaurantForm.city} *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="form-input w-full"
                    data-testid="input-city"
                  />
                </div>
              </div>
            </div>

            {/* Requirements */}
            <div className="bg-card border border-primary/10 rounded-sm p-6 md:p-8">
              <h2 className="text-xl font-serif text-foreground mb-6 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-sm">2</span>
                {t.restaurantForm.section2}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t.restaurantForm.pizzaType} *
                  </label>
                  <select
                    required
                    value={formData.pizza_type_needed}
                    onChange={(e) => setFormData({ ...formData, pizza_type_needed: e.target.value })}
                    className="form-select w-full"
                    data-testid="select-pizza-type"
                  >
                    <option value="">Seleziona...</option>
                    {pizzaTypes.map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t.restaurantForm.ovenType} *
                  </label>
                  <select
                    required
                    value={formData.oven_type}
                    onChange={(e) => setFormData({ ...formData, oven_type: e.target.value })}
                    className="form-select w-full"
                    data-testid="select-oven-type"
                  >
                    <option value="">Seleziona...</option>
                    {ovenTypes.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-foreground mb-3">
                  {t.restaurantForm.experience} *
                </label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {experienceLevels.map(level => (
                    <button
                      key={level.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, experience_required: level.value })}
                      className={`p-3 text-sm rounded-sm border transition-all duration-200 ${
                        formData.experience_required === level.value
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'border-primary/20 text-foreground hover:border-primary/40'
                      }`}
                      data-testid={`exp-req-${level.value}`}
                    >
                      {level.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Conditions */}
            <div className="bg-card border border-primary/10 rounded-sm p-6 md:p-8">
              <h2 className="text-xl font-serif text-foreground mb-6 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-sm">3</span>
                {t.restaurantForm.section3}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t.restaurantForm.salary} *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="es. 2000-2500€/mese"
                    value={formData.salary_range}
                    onChange={(e) => setFormData({ ...formData, salary_range: e.target.value })}
                    className="form-input w-full"
                    data-testid="input-salary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t.restaurantForm.urgency} *
                  </label>
                  <select
                    required
                    value={formData.urgency}
                    onChange={(e) => setFormData({ ...formData, urgency: e.target.value })}
                    className="form-select w-full"
                    data-testid="select-urgency"
                  >
                    <option value="">Seleziona...</option>
                    {urgencyLevels.map(u => (
                      <option key={u.value} value={u.value}>{u.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.accommodation_provided}
                    onChange={(e) => setFormData({ ...formData, accommodation_provided: e.target.checked })}
                    className="w-5 h-5 rounded-sm border-primary/30 bg-muted text-primary focus:ring-primary"
                    data-testid="checkbox-accommodation"
                  />
                  <span className="text-foreground">{t.restaurantForm.accommodation}</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t.restaurantForm.notes}
                </label>
                <textarea
                  rows={4}
                  value={formData.additional_notes}
                  onChange={(e) => setFormData({ ...formData, additional_notes: e.target.value })}
                  className="form-input w-full resize-none"
                  data-testid="input-notes"
                />
              </div>
            </div>

            {/* Submit */}
            <div className="mb-6">
              <label className="flex items-center gap-3 cursor-pointer justify-center">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="w-5 h-5 rounded-sm border-primary/30 bg-muted text-primary focus:ring-primary"
                  data-testid="checkbox-consent"
                />
                <span className="text-foreground text-sm">
                  Accetto la <a href="/privacy-policy" target="_blank" className="text-primary hover:underline">Privacy Policy</a> e i <a href="/termini-servizio" target="_blank" className="text-primary hover:underline">Termini di Servizio</a>
                </span>
              </label>
            </div>

            <div className="text-center">
              <button
                type="submit"
                disabled={loading || !consent || !formData.experience_required}
                className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold uppercase tracking-widest px-12 py-4 rounded-sm btn-glow hover:brightness-110 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-restaurant-form"
              >
                {loading ? 'Invio in corso...' : t.restaurantForm.submit}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
}
